<template>
  <section class="flex flex-col gap-8 md:mx-14 mx-5" test-id="main">
    <div class="flex items-center gap-x-5 mt-5">
      <div
        class="flex items-center gap-x-2 border rounded-lg px-2.5 py-1 cursor-pointer"
        @click="goBack"
      >
        <back-arrow />
        <p class="text-black text-13px font-normal">Back</p>
      </div>
      <div class="flex items-center gap-x-2.5">
        <bread-crumb-icon />
        <arrow-left class="w-4 h-4" />
        <p class="text-13px font-medium text-fg-muted">Accounts</p>
      </div>
    </div>
    <div v-if="isLoading">
      <accounts-loader />
    </div>
    <div v-else class="flex flex-col gap-5">
      <div>
        <!-- Accounts -->
        <div class="flex lg:flex-row flex-col justify-between items-center">
          <div
            class="grid grid-cols-12 gap-x-2 background py-1.5 px-2 rounded-lg mb-5 lg:mb-0"
          >
            <div
              @click="setActiveClass($STATUS.ACTIVE)"
              class="col-span-4 flex justify-center text-13px py-2.5 px-5 cursor-pointer transition-all duration-150 ease-linear"
              :class="
                isActiveAccounts
                  ? 'bg-white rounded-md text-primary font-bold shadow-md'
                  : 'font-normal text-fg-muted'
              "
            >
              Active
            </div>
            <div
              @click="setActiveClass($STATUS.PENDING)"
              class="col-span-4 flex justify-center text-13px py-2.5 px-5 cursor-pointer"
              :class="
                isPendingAccounts
                  ? 'bg-white rounded-md text-primary font-bold shadow-md'
                  : 'font-normal text-fg-muted'
              "
            >
              Pending
            </div>
            <div
              @click="setActiveClass($STATUS.FROZEN)"
              class="col-span-4 flex justify-center text-13px py-2.5 px-5 cursor-pointer"
              :class="
                isFrozenAccounts
                  ? 'bg-white rounded-md text-primary font-bold shadow-md'
                  : 'font-normal text-fg-muted'
              "
            >
              Frozen
            </div>
          </div>
          <div class="flex items-center gap-x-3">
            <div
              class="flex items-center gap-x-2 bg-primary text-white rounded-lg py-2.5 px-3 md:text-sm text-xs cursor-pointer"
              @click="hasCompletedKyc ? toggleShowCountryOptions() : {}"
            >
              <plus-icon-2 class="w-4 h-4" color="#fff" />
              <p class="font-normal">Request new account</p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div v-if="accountsIsEmpty">
          <transaction-empty-state>
            <h3 class="font-semibold text-root-2">No result found.</h3>
            <p
              class="font-light text-root-4 text-xs flex items-center justify-center mt-2"
            >
              <span class="text-center break-normal"
                >There are no {{ activeClass }} accounts.</span
              >
            </p>
          </transaction-empty-state>
        </div>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 items-center w-full relative"
        >
          <!-- available accounts -->
          <div v-for="(account, index) in accounts" :key="index">
            <tooltip
              :disabled="hasCompletedKyc"
              :content="disabledKycTooltipContent"
            >
              <div
                class="flex gap-x-4 border border-main bg-surface px-4 py-6 h-25 rounded-lg w-full cursor-pointer transition-all duration-300 hover:shadow-xl relative"
                :class="hasKycDisabledClass"
                v-on="
                  hasCompletedKyc && activeClass !== $STATUS.PENDING
                    ? { click: () => goToAccountDetails(account.currency) }
                    : {}
                "
              >
                <currency-icons
                  :currency="getCurrencyCountry(account.currency)"
                  size="lg:h-7 lg:w-7 h-6 w-6 rounded-full"
                />
                <tooltip
                  :disabled="false"
                  :content="activeClass"
                  placement="top"
                  v-if="activeClass"
                >
                  <div class="absolute top-3 right-3">
                    <pending-icon v-if="activeClass === $STATUS.PENDING" />
                    <frozen-icon v-else-if="activeClass === $STATUS.FROZEN" />
                  </div>
                </tooltip>
                <div>
                  <p class="font-light text-xs mb-1 text-root-2">
                    Total Balance
                  </p>
                  <h3 class="font-extrabold text-md text-root-2">
                    {{
                      parseMoneyAll(account.currentBalance, account.currency)
                    }}
                  </h3>
                </div>
              </div>
            </tooltip>
          </div>
        </div>
      </div>
    </div>
    <searchable-currency-select-dialog
      v-if="showCountryOptions"
      :show-dialog="showCountryOptions"
      :options="countries"
      :value="searchCountry"
      @input="handleSearchInput"
      @optionSelected="handleSelectCountry"
      @close="toggleShowCountryOptions"
    />
    <dashboard-drawer
      v-if="showDashboardDrawer"
      :showDrawer="showDashboardDrawer"
      @closeDrawer="closeDrawer('openDrawer')"
      drawerContent="coming-soon-content"
    />
  </section>
</template>

<script>
import { GetCurrencyCountry, ParseMoneyAll } from '@/utils/helpers.js'
import tenant from '@/mixins/tenant.js'
import getBalance from '@/mixins/getBalance'
import virtualAccounts from '@/mixins/virtualAccounts.js'
import getDashboardReports from '@/mixins/getDashboardReports.js'
import { SUPPORTED_REQUEST_CURRENCIES } from '@/utils/constants'

export default {
  data () {
    return {
      activeClass: 'active',
      isLoading: false,
      showCountryOptions: false,
      countries: [],
      searchCountry: '',
      disabledKycTooltipContent:
        'KYC is yet to be approved, currently under review',
      showDashboardDrawer: false,
      accounts: [],
      supportedRequestCurrencies: SUPPORTED_REQUEST_CURRENCIES
    }
  },
  mixins: [tenant, getBalance, virtualAccounts, getDashboardReports],
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    BreadCrumbIcon: () => import('@/assets/v2/icons/BreadCrumbIcon.vue'),
    BackArrow: () => import('@/assets/icons/BackArrow.vue'),
    ArrowLeft: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    PlusIcon2: () => import('@/assets/v2/icons/PlusIcon2.vue'),
    SearchableCurrencySelectDialog: () =>
      import('@/components/v2/dashboard/SearchableCurrencySelectDialog.vue'),
    DashboardDrawer: () =>
      import('@/components/v2/dashboard/DashboardDrawer.vue'),
    TransactionEmptyState: () =>
      import('@/components/empty-states/TransactionEmptyState.vue'),
    FrozenIcon: () => import('@/assets/v2/icons/FrozenIcon.vue'),
    PendingIcon: () => import('@/assets/v2/icons/PendingIcon.vue'),
    AccountsLoader: () =>
      import('@/components/v2/skeletalLoaders/AccountsLoader.vue')
  },
  async mounted () {
    this.isLoading = true
    await this.getDashboardReports()
    await this.fetchAllVirtualAccountRequests()
    await this.getVirtualAccountOpenings()
    await this.getFrozenAccountBalances()
    this.accounts = this.sortedAccounts
    this.isLoading = false
  },
  methods: {
    goBack () {
      this.$router.push('/dashboard')
    },
    setActiveClass (arg) {
      this.activeClass = arg
    },
    clearSearchCountry () {
      this.searchCountry = ''
    },
    toggleShowCountryOptions () {
      this.showCountryOptions = !this.showCountryOptions
      this.clearSearchCountry()
    },
    handleSelectCountry (country) {
      this.toggleShowCountryOptions()
      this.isLoading = true
      this.$store
        .dispatch('transactions/generateVirtualAccount', {
          currency: country.currency
        })
        .then(async () => {
          this.$notify({
            title: 'Request Received',
            message:
              'You will get an email notification upon approval of your account request.',
            type: 'success'
          })
          await this.fetchAllVirtualAccountRequests()
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleSearchInput (newValue) {
      this.searchCountry = newValue
    },
    goToAccountDetails (currency) {
      this.$store.dispatch('report/setAccountDetailsCurrency', currency)
      this.$router.push('/dashboard/account-details')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    openDrawer () {
      this.showDashboardDrawer = true
    },
    closeDrawer (arg) {
      this.showDashboardDrawer = false
    },
    getVirtualAccountRequests () {
      this.store.dispatch('')
    },
    getStatusIcon () {
      return 'FrozenIcon'
    }
  },
  computed: {
    isActiveAccounts () {
      return this.activeClass === this.$STATUS.ACTIVE
    },
    sortedAccounts () {
      // sort accounts in alphabetical order by their currency
      const sortedAccounts = this.filteredBalanceAccounts
        .slice()
        .sort((a, b) => a.currency.localeCompare(b.currency))
      return sortedAccounts
    },
    isPendingAccounts () {
      return this.activeClass === this.$STATUS.PENDING
    },
    isFrozenAccounts () {
      return this.activeClass === this.$STATUS.FROZEN
    },
    hasCompletedKyc () {
      return this.$store.state.auth.business.completedKyc
    },
    hasKycDisabledClass () {
      return this.hasCompletedKyc
        ? 'opacity-100 hover:opacity-80'
        : 'cursor-not-allowed opacity-80 hover:opacity-70'
    },
    supportedCurrencyOptions () {
      return this.filteredCurrencyOptions.filter(
        (country) =>
          !this.filteredBalanceAccounts.find(
            (currency) => currency.currency === country.currency
          ) &&
          !this.pendingVirtualAccountRequests.find(
            (currency) => currency.currency === country.currency
          )
      )
    },
    accountsIsEmpty () {
      return this.accounts.length === 0
    }
  },
  watch: {
    searchCountry: {
      handler (newVal, oldVal) {
        const formattedSearchCountry = newVal.toLowerCase()
        this.countries = this.supportedCurrencyOptions.filter(
          (country) =>
            country.currency.toLowerCase().indexOf(formattedSearchCountry) >
              -1 ||
            country.currencyFull.toLowerCase().indexOf(formattedSearchCountry) >
              -1
        )
      },
      immediate: true
    },
    activeClass: {
      handler (newVal) {
        if (newVal === this.$STATUS.PENDING) {
          this.accounts = this.pendingVirtualAccountRequests
        } else if (newVal === this.$STATUS.FROZEN) {
          this.accounts = this.frozenVirtualAccounts
        } else {
          this.accounts = this.sortedAccounts
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.text {
  position: absolute;
  z-index: 30;
  top: 16px;
}

.background {
  background: var(--background-muted);
}

>>> .el-input__inner {
  border: none !important;
  color: var(--foreground-default);
}

.border-colour {
  border: 1px solid var(--input-border-default);
}

>>> .el-select-dropdown__item {
  width: auto !important;
}
</style>
